import { useEffect } from 'react';
import gql from 'graphql-tag';
import { useSubscription } from '@apollo/react-hooks';

import { getAGGClient, markErrorAsHandled } from '@confluence/graphql';
import { getConfluenceContentAri } from '@confluence/content-types-utils';
import { useSessionData } from '@confluence/session-data';
import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { getMonitoringClient, setContentErrorAttributes } from '@confluence/monitoring';
import { Attribution, getStatusCode } from '@confluence/error-boundary';

const subscription = gql`
	subscription ContentStateUpdatedSubscription($id: ID!) {
		confluence_onContentModified(id: $id) {
			type
			id
			__typename
			_deltas
			contentStateUpdated {
				key
				value
				version
				__typename
			}
		}
	}
`;

export const onContentStateUpdatedSubscriptionData = (
	response: any,
	onDataReceived: (contentState: any) => void,
	contentId: string,
	contentType: 'page' | 'blogpost' | null,
) => {
	getAnalyticsWebClient().then((client) => {
		client.sendOperationalEvent({
			action: 'received',
			actionSubject: 'subscription.event',
			actionSubjectId: 'contentStateUpdated',
			source: 'contentStateUpdatedSubscription',
			attributes: {
				contentId,
				contentType,
			},
		});
	});

	const data = response?.subscriptionData?.data;
	const _deltas = data?.confluence_onContentModified?._deltas;
	if (!data || !data?.confluence_onContentModified || !Array.isArray(_deltas)) {
		return;
	}
	if (_deltas?.length === 0 || _deltas.indexOf('contentStateUpdated') === -1) {
		return;
	}

	const { contentStateUpdated } = data?.confluence_onContentModified;
	const contentState = JSON.parse(contentStateUpdated?.value);
	onDataReceived(contentState.contentState);
};

export const ContentStateUpdatedSubscription = ({
	contentType,
	contentId,
	onDataReceived,
	skip = false,
}: {
	contentType: 'page' | 'blogpost' | null;
	contentId: string;
	onDataReceived: (contentState: any) => void;
	skip?: boolean;
}) => {
	const { activationId, cloudId } = useSessionData();

	const ari = getConfluenceContentAri({
		siteId: cloudId,
		resourceType: contentType || '',
		activationId,
		resourceId: contentId,
	});

	const { error } = useSubscription(subscription, {
		variables: {
			id: ari,
		},
		onSubscriptionData(response) {
			onContentStateUpdatedSubscriptionData(response, onDataReceived, contentId, contentType);
		},
		skip: skip || !ari || !contentId || !contentType || !activationId,
		client: getAGGClient(),
	});

	useEffect(() => {
		if (error) {
			const errorMessage = error?.message || (error as any)?.errors?.[0]?.message; // error object can be an array
			const statusCode =
				getStatusCode(error) || (error as any)?.errors?.[0]?.extensions?.statusCode;
			if (statusCode !== 403) {
				const e = new Error(`Subscription Error - ContentStateUpdatedSubscription:${errorMessage}`);
				setContentErrorAttributes(e, {
					objectId: contentId,
					objectType: contentType,
				});
				getMonitoringClient().submitError(e, {
					attribution: Attribution.PAGE_EXPERIENCES,
				});
				markErrorAsHandled(error);
			}
		}
	}, [error, contentId, contentType]);

	return null;
};
